import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { Button } from '../components/ui/Button';
import Dialog from '../components/ui/Dialog';
import { PinCode } from '../components/ui/DialogPin';
import FadeIn from '../components/ui/FadeIn';
import { Field } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';

interface Props {
  hasPin?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 1 | 2 | 3;

const PIN_LENGTH = 4;

const PinModal: React.FC<Props> = ({ hasPin, onClose, onSuccess }) => {
  const api = useApi();
  const toast = useToast();

  // The parent mounts this only while it is open, so it closes itself and reports once its exit has run.
  const [open, setOpen] = useState(true);
  const after = useRef<() => void>(onClose);
  const leave = (then: () => void) => { after.current = then; setOpen(false); };

  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState<Step>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const confirmRef = useRef<{ focus: () => void }>(null);

  // After a mismatch the emptied row takes focus once it has rendered empty.
  useEffect(() => {
    if (mismatch && !confirmPin) confirmRef.current?.focus();
  }, [mismatch, confirmPin]);

  const submit = async () => {
    setError('');
    if (step === 1) {
      if (!password) { setError('Enter your password to go on.'); return; }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (pin.length !== PIN_LENGTH) { setError(`Enter all ${PIN_LENGTH} digits.`); return; }
      setStep(3);
      return;
    }
    if (confirmPin.length !== PIN_LENGTH) { setError(`Enter all ${PIN_LENGTH} digits.`); return; }
    if (pin !== confirmPin) {
      setError('The two PINs differ. Type the new PIN again.');
      setMismatch(true);
      setConfirmPin('');
      return;
    }

    setSaving(true);
    try {
      await api.post('/users/me/pin', { body: { password, pin } });
      toast.success(hasPin ? 'PIN updated' : 'PIN set');
      leave(onSuccess);
    } catch (err: any) {
      console.error('Error setting PIN:', err);
      const msg: string = err?.message || 'The PIN did not save. Try again.';
      if (msg.includes('password') || msg.includes('Invalid')) {
        setError('That password is not right. Enter it again.');
        setStep(1);
        setPassword('');
        setPin('');
        setConfirmPin('');
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const nextLabel = step < 3 ? 'Continue' : saving ? 'Saving' : hasPin ? 'Update PIN' : 'Set PIN';
  const nextHint = step === 1 ? 'Go on to choosing a PIN' : step === 2 ? 'Go on to typing it again' : 'Save it for Fireflasher sign-in';
  const waiting = (step === 2 && pin.length !== PIN_LENGTH) || (step === 3 && confirmPin.length !== PIN_LENGTH);

  return (
    <Dialog
      open={open}
      onClose={() => leave(onClose)}
      onExited={() => after.current()}
      title={hasPin ? 'Change PIN' : 'Set PIN'}
      meta={`Step ${step} of 3`}
      size="sm"
      busy={saving}
      dismissible={false}
      dirty={password !== '' || pin !== '' || confirmPin !== ''}
      closeHint="Close without changing your PIN"
      onSubmit={submit}
      problem={error || null}
      footer={(
        <>
          <Button variant="tonal" disabled={saving} onClick={() => leave(onClose)}>Cancel</Button>
          <Tooltip label={nextHint} note={waiting ? `Enter all ${PIN_LENGTH} digits first` : undefined}>
            <Button type="submit" variant="filled" disabled={saving || waiting}>{nextLabel}</Button>
          </Tooltip>
        </>
      )}
    >
      <FadeIn key={step}>
        {step === 1 && (
          <>
            <label htmlFor="pin-password" className="mb-1.5 block text-[13px] font-medium leading-[18px] text-on-surface-variant">
              Your password
            </label>
            <Field
              id="pin-password"
              type="password"
              autoFocus
              autoComplete="current-password"
              value={password}
              error={!!error}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
            />
            <p className="m-0 mt-1.5 text-xs text-on-surface-variant">Enter it to {hasPin ? 'change' : 'set'} the PIN you sign in to Fireflasher with.</p>
          </>
        )}
        {step === 2 && (
          <>
            <p className="m-0 mb-3 text-sm text-on-surface-variant">Choose a {PIN_LENGTH}-digit PIN.</p>
            <PinCode
              label="New PIN"
              length={PIN_LENGTH}
              autoFocus
              autoComplete="new-password"
              value={pin}
              onChange={(v) => { setPin(v); setError(''); }}
            />
          </>
        )}
        {step === 3 && (
          <>
            <p className="m-0 mb-3 text-sm text-on-surface-variant">Type the same PIN again.</p>
            <PinCode
              ref={confirmRef}
              label="New PIN again"
              length={PIN_LENGTH}
              autoFocus
              autoComplete="new-password"
              value={confirmPin}
              error={mismatch}
              onChange={(v) => { setConfirmPin(v); setError(''); setMismatch(false); }}
            />
          </>
        )}
      </FadeIn>
    </Dialog>
  );
};

export default PinModal;
